import { InjectionToken, Injector, ViewContainerRef } from '@angular/core';

/** Injection token for passing data to a dialog component. */
export const DIALOG_DATA = new InjectionToken<unknown>('DialogData');

/** Possible ARIA roles for the dialog container. */
export type DialogRole = 'dialog' | 'alertdialog';

/** Configuration for opening a dialog via DialogService. */
export interface DialogConfig<D = unknown> {
  /** Data to pass to the dialog component via DIALOG_DATA injection. */
  data?: D;

  /** Width of the dialog. */
  width?: string;

  /** Height of the dialog. */
  height?: string;

  /** Minimum width of the dialog. */
  minWidth?: string | number;

  /** Maximum width of the dialog. */
  maxWidth?: string | number;

  /** Minimum height of the dialog. */
  minHeight?: string | number;

  /** Maximum height of the dialog. */
  maxHeight?: string | number;

  /** Position overrides (top/bottom/left/right). */
  position?: DialogPosition;

  /** CSS class(es) to apply to the overlay pane. */
  panelClass?: string | string[];

  /** CSS class(es) to apply to the backdrop. */
  backdropClass?: string | string[];

  /** Whether the dialog has a backdrop. Default: true. */
  hasBackdrop?: boolean;

  /** Whether clicking the backdrop closes the dialog. Default: false (closing allowed). */
  disableClose?: boolean;

  /** Where to auto-focus on open. Default: 'first-tabbable'. */
  autoFocus?: AutoFocusTarget | string | boolean;

  /** Whether navigating away closes the dialog. Default: true. */
  closeOnNavigation?: boolean;

  /** ARIA role of the dialog container. Default: 'dialog'. */
  role?: DialogRole;

  /** Accessible label, used when the dialog has no title (aria-labelledby). */
  ariaLabel?: string;

  /** Unique ID for the dialog. Auto-generated when not provided. */
  id?: string;

  /**
   * ViewContainerRef to use as the parent for the dialog component.
   * Provides access to the caller's injector hierarchy (needed for
   * lazy-loaded module providers).
   */
  viewContainerRef?: ViewContainerRef;

  /** Custom injector to use as the parent for the dialog component. */
  injector?: Injector;
}

export interface DialogPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}

export type AutoFocusTarget = 'dialog' | 'first-tabbable' | 'first-heading';
